import React from 'react';
import PropTypes from 'prop-types';
import pick from 'lodash/pick';
import {
  Route, Switch, Redirect, withRouter,
} from 'react-router-dom';
import { connect } from 'react-redux';
import cx from 'classnames';
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import configs from 'configs';
import { Popups } from 'components/Popups/Popups';
import { Alert } from 'components/Alert';
import { GA_SIGNUP_CATEGORY, GA_SIGNUP } from 'modules/ga/generalEvents';
import { PENDING, SUCCESS } from 'utils/constants/queryStatuses';
import { PROVIDER_URL_SEARCH_PARAM } from 'utils/constants/urlSearchParameters';
import PageLoader from 'components/PageLoader';
import Splash from 'components/Splash';
import { SimpleBackdrop } from 'components/SimpleBackdrop/SimpleBackdrop';
import { shouldHideNav } from 'components/NavBar/functions';
import { BACKDROP_TYPES } from 'modules/backdrop/constants';
import { saveProviderInfo } from 'modules/user/api';
import mainConstants from 'utils/constants/main';
import { useIsMobileResolution } from 'utils/hooks/useIsMobileResolution';
import { ROUTE_NAME, ROUTE_PATH } from 'utils/constants/router';
import analyticsService from 'functions/analytics';
import { FB_EVENTS, FB_TRACK_TYPE } from 'functions/analytics/constants/fbKeys';
import { QuestionnairePage } from 'pages/QuestionnairePage';
import 'main.css';
import { Preloader } from 'components/Preloader';
import app from 'new-ui/app';
import { getBackdropTypeToShow, getIsDashboardRoute, shouldHideFooter } from './functions';
import { ExternalLogoutListener } from './ExternalLogoutListener';
import { AppLogic } from './AppLogic';
import Dashboard from './components/Dashboard';
import PrivateRoute from './components/PrivateRoute';
import ProtectedRoute from './components/ProtectedRoute';
import AppRouteContainer from './components/AppRouteContainer';
import { ScrollManager } from './ScrollManager';

// DEV-1001, DEV-1323
// ?provider=mpnrf&utm_source=facebook&refid=fb123&utm_term=BULLDOZER
// corresponds to the following columns in userFlowStatistic model (ps)
// providerId: 'provider'
// utm_source: 'utm_source'
// ref_id: 'refid'
// utm_term: 'utm_term'
// ctm: 'ctm'
const USER_FLOW_STATISTIC_URL_SEARCH_PARAMS = [
  PROVIDER_URL_SEARCH_PARAM,
  'utm_source',
  'refid',
  'utm_term',
  // ctm
  // 'redirectTo'
];

const Home = PageLoader({
  loader: () => import('pages/Home/Home'),
});
const Signup = PageLoader({
  loader: () => import('components/Authentication/components/Signup'),
});
const AfterSignupUserDetails = PageLoader({
  loader: () => import('components/Authentication/components/AfterSignupUserDetails/AfterSignupUserDetails'),
});
const Login = PageLoader({
  loader: () => import('components/Authentication/components/Login'),
});
const ForgotPassword = PageLoader({
  loader: () => import('components/Authentication/components/ForgotPassword'),
});
const NewPassword = PageLoader({
  loader: () => import('components/Authentication/components/NewPassword'),
});
const Footer = PageLoader({
  loader: () => import('components/Footer'),
});
const NavBar = PageLoader({
  loader: () => import('components/NavBar'),
});

const useStyles = makeStyles(() => ({
  appRouteContent: {
    minHeight: 'calc(100vh - 60px)',
  },
  providerAppRouteContent: {
    height: '100%',
    background: 'white',
    boxShadow: 'rgb(233 222 226) 0px 0px 5px 0px',
    overflow: 'hidden',
  },
  appMain: {
    width: '100%',
  },
  providerAppMain: {
    width: '100%',
    margin: '0 auto',
    overflowY: 'auto',
    height: 'calc(100% - 30px) !important',
  },
  appMainContainer: {
    flexGrow: '1',
  },
  providerAppMainContainer: {
    width: '100%',
    flexGrow: '1',
    background: 'transparent',
    height: '100% !important',
  },
  providerWrapper: {
    height: '100%',
  },
}));

const PreloaderBackdrop = () => <Preloader isVisible />;

const renderBackdrop = (type) => {
  const Component = {
    [BACKDROP_TYPES.PRELOADER]: PreloaderBackdrop,
    [BACKDROP_TYPES.SPLASH]: Splash,
    [BACKDROP_TYPES.SIMPLE_BACKDROP]: SimpleBackdrop,
  }[type];
  return Component ? <Component /> : null;
};

const AppRouteContent = ({
  location,
  questionnaire,
  provider,
}) => {
  const classes = useStyles({ provider });
  const isMobile = useIsMobileResolution();
  const isFooterShown = !shouldHideFooter({
    pathname: location.pathname,
    isMobile,
    provider,
    isQuestionnaireInitialized: questionnaire.isInitialized,
  });

  return (
    <AppRouteContainer>
      <div className={cx(classes.appRouteContent, { [classes.providerAppRouteContent]: provider })}>
        {!shouldHideNav() && <NavBar />}
        <Grid className={provider ? classes.providerAppMainContainer : classes.appMainContainer} container>
          <main
            className={provider ? classes.providerAppMain : classes.appMain}
          >
            <div className={cx({ [classes.providerWrapper]: provider })}>
              <Switch>
                <Route
                  exact
                  path={ROUTE_PATH[ROUTE_NAME.HOME]}
                  component={Home}
                />
                <ProtectedRoute path={ROUTE_PATH[ROUTE_NAME.QUESTIONNAIRE]} component={QuestionnairePage} />
                <ProtectedRoute
                  isProfileRequired
                  notRegistered
                  path={ROUTE_PATH[ROUTE_NAME.SIGN_UP]}
                  component={Signup}
                />
                <PrivateRoute
                  path={ROUTE_PATH[ROUTE_NAME.AFTER_SIGNUP_USER_DETAILS]}
                  component={AfterSignupUserDetails}
                />
                <Route path={ROUTE_PATH[ROUTE_NAME.FORGOT_PASSWORD]} component={ForgotPassword} exact />
                <Route path={ROUTE_PATH[ROUTE_NAME.CREATE_PASSWORD]} component={ForgotPassword} exact />
                <Route path={ROUTE_PATH[ROUTE_NAME.NEW_PASSWORD]} component={NewPassword} exact />
                <Route path={ROUTE_PATH[ROUTE_NAME.LOGIN]} component={Login} exact />
                <Redirect to={ROUTE_PATH[ROUTE_NAME.HOME]} />
              </Switch>
            </div>
          </main>
        </Grid>
      </div>
      {isFooterShown ? <Footer /> : null}
    </AppRouteContainer>
  );
};

class AppRoutes extends React.Component {
  isProviderInfoSaved = false;

  componentDidUpdate(prevProps) {
    const {
      loggedIn,
      cameFrom,
      urlParameters,
      dispatch,
      lastOpenedUrl,
      appConfigurationQueryStatus,
    } = this.props;
    const { appConfigurationQueryStatus: prevAppConfigurationQueryStatus } = prevProps;

    const url = window?.location.href || '';
    if (lastOpenedUrl !== url) {
      dispatch({
        type: mainConstants.SET_LAST_OPENED_URL,
        payload: url,
      });

      const eventID = `${FB_EVENTS.PAGE_VIEW}.${Date.now()}`;
      if (
        typeof window.fbq === 'function'
        && !!configs.metaPixelId
      ) {
        window.fbq(
          FB_TRACK_TYPE.TRACK_SINGLE,
          configs.metaPixelId,
          FB_EVENTS.PAGE_VIEW,
          {},
          { eventID },
        );
      }
      analyticsService.sendFbEventToConversionApi({
        eventName: FB_EVENTS.PAGE_VIEW,
        eventID,
      }).catch((e) => console.error('Can\'t send facebook event', e));
    }

    if (!loggedIn && prevAppConfigurationQueryStatus === PENDING && appConfigurationQueryStatus === SUCCESS) {
      // wait for appConfiguration.providerSpecific to be resolved
      app.sendGoogleAnalyticsEvent(GA_SIGNUP_CATEGORY, GA_SIGNUP.VISITOR_LANDED);
    }

    if (
      loggedIn
      && !this.isProviderInfoSaved
      // shouldn't provider urlParameter be mandatory?
      && USER_FLOW_STATISTIC_URL_SEARCH_PARAMS.some((condition) => urlParameters?.includes(condition))
    ) {
      // optimistically
      this.isProviderInfoSaved = true;
      saveProviderInfo({
        cameFrom,
        urlParameters: urlParameters.split('?').pop(),
      }).catch((ex) => {
        this.isProviderInfoSaved = false;
        console.error('Can\'t save provider info', ex);
      });
    }
  }

  render() {
    const {
      location,
      questionnaire,
      provider,
      backdropTypeToShow,
    } = this.props;

    const isDashboard = getIsDashboardRoute(location.pathname);

    document.body.setAttribute('mode', isDashboard ? 'dashboard' : 'questionnaire');

    const isBackdropShown = !!backdropTypeToShow;

    return (
      <>
        <AppLogic />
        <ExternalLogoutListener />
        <ScrollManager />
        {isBackdropShown && renderBackdrop(backdropTypeToShow)}
        <Popups />
        <Alert />
        <div id="router" inert={isBackdropShown ? '' : undefined}>
          {isDashboard ? (
            <Dashboard />
          ) : (
            <AppRouteContent
              location={location}
              questionnaire={questionnaire}
              provider={provider}
            />
          )}
        </div>
      </>
    );
  }
}

AppRouteContent.propTypes = {
  location: PropTypes.object.isRequired,
  questionnaire: PropTypes.object.isRequired,
  provider: PropTypes.string,
};

AppRoutes.propTypes = {
  location: PropTypes.object.isRequired,
  questionnaire: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  backdropTypeToShow: PropTypes.oneOf(Object.values(BACKDROP_TYPES)),
  loggedIn: PropTypes.bool,
  cameFrom: PropTypes.string,
  urlParameters: PropTypes.string,
  provider: PropTypes.string,
  appConfigurationQueryStatus: PropTypes.string.isRequired,
  lastOpenedUrl: PropTypes.string,
};

const StyledAppRoutes = withRouter(AppRoutes);

const mapStateToProps = (state) => ({
  questionnaire: state.questionnaire,
  backdropTypeToShow: getBackdropTypeToShow(pick(state, ['authentication', 'userGet', 'userUpdate', 'backdrop'])),
  loggedIn: state?.authentication?.loggedIn,
  cameFrom: state.main.cameFrom,
  urlParameters: state.main.urlParameters,
  provider: state?.appConfiguration?.providerSpecific?.code,
  appConfigurationQueryStatus: state?.appConfiguration.status,
  lastOpenedUrl: state?.main?.lastOpenedUrl,
});

export default connect(mapStateToProps)(StyledAppRoutes);
